import { once } from "@/fp";
import * as api from "./api";
import {
  CognniCsvReport,
  CognniCsvReportHelper,
  CsvScheduleItem,
} from "./model";
import { UseCollection } from "@/common/composition/use-collection";
import { state as data_sources_state } from "@/views/customize/data-sources/data/state";
import { GqlCallsKey } from "@/mock/demo/gql.resolver";
import { ITableConfig } from "@/components/table/table.model";
import { GqlCache } from "@/gql/lib";
import { UseModal } from "@/components/root/modal";
import GenerateCsvReportModal from "@/views/reports/csv-reports/GenerateCsvReportModal.vue";
import ScheduleCsvReportModal from "@/views/reports/csv-reports/ScheduleCsvReportModal.vue";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";
import { NotificationsManager } from "@/components/root/navbar/notifications/NotificationManager";
import router from "@/router";
import { SharedState } from "@/common/state";
import { Ref, ref } from "@vue/composition-api";
import { trackReportEvent } from "../report-analytics";

const csv_reports = UseCollection<CognniCsvReport, { cursor: string }>(
  api.load_all_csv_reports
);

const scheduled_reports: Ref<CsvScheduleItem[]> = ref([]);

const remove_scheduled_report = async (ruleId: string) => {
  const report = scheduled_reports.value.find((item) => item.ruleId === ruleId);
  const res = await api.remove_csv_report_schedule(ruleId);
  if (res.success) {
    scheduled_reports.value = scheduled_reports.value.filter(
      (report) => report.ruleId !== ruleId
    );
    if (report) {
      trackReportEvent("schedule_deleted", {
        report_id: report.ruleId,
        report_name: report.name,
        report_type: "scheduled",
        report_subtype: "csv",
        parameters: { description: report.description },
        schedule: {
          cadence: report.timeFrame,
          recipient_count: report.subscribers.length,
        },
        source: "scheduled_csv_reports",
      });
    }
  }
};
const update_scheduled_report = async (report: CsvScheduleItem) => {
  const res = await api.update_csv_report_schedule({
    ruleId: report.ruleId,
    subscribers: report.subscribers,
    timeFrame: report.timeFrame,
  });
  if (res.success) {
    scheduled_reports.value = scheduled_reports.value.map((item) => {
      if (item.ruleId === report.ruleId) {
        return report;
      }
      return item;
    });
    UseToast().show({
      type: ToastType.SUCCESS,
      msg: "report updated successfully",
      duration: 3000,
    });
    trackReportEvent("schedule_updated", {
      report_id: report.ruleId,
      report_name: report.name,
      report_type: "scheduled",
      report_subtype: "csv",
      parameters: { description: report.description },
      schedule: {
        cadence: report.timeFrame,
        recipient_count: report.subscribers.length,
      },
      source: "scheduled_csv_reports",
    });
  }
};

const strip_off_cursor = (query: string) => {
  return query.replace(/cursor:.*?(\n|\s)/g, "");
};

const schedule_gql_csv_report = async (payload: {
  possible_call_tags: GqlCallsKey[];
  title: string;
  table_config: ITableConfig;
  tags: Record<string, string | number>;
}) => {
  init();
  const { possible_call_tags, table_config, tags, title } = payload;
  const mappings = table_config.columns.reduce((acc, col) => {
    if (col.jsPath) {
      acc.push({ [col.jsPath]: col.name });
    }
    return acc;
  }, [] as Array<Record<string, string>>);
  console.log("mappings", mappings);

  const query = GqlCache.get_query_from_last_tag_out_of_many(
    possible_call_tags
  );

  console.log(mappings, strip_off_cursor(query));
  const keys = mappings.map((item) => Object.values(item)[0]);
  const res = await UseModal().show(ScheduleCsvReportModal, { keys, title });
  if (res.type == "success") {
    const {
      keys,
      name,
      description = "",
      subscribers = [],
      timeFrame,
    } = (res.msg! as unknown) as {
      keys: string[];
      name: string;
      description: string;
      subscribers: string[];
      timeFrame: "weekly" | "monthly";
    };
    const create_csv_res = await api.schedule_csv_report({
      name,
      description,
      subscribers,
      timeFrame,
      query: {
        type: "GraphQL",
        graphql: {
          map: mappings.filter((item) => keys.includes(Object.values(item)[0])),
          query,
        },
        // get the tags from the filters
        tags: Object.entries(tags)
          // remove empty values
          .filter(([, v]) => v)
          .map(([key, value]) => `${key}:${value}`),
      },
    });
    if (create_csv_res.success) {
      trackReportEvent("scheduled", {
        report_name: name,
        report_type: "scheduled",
        report_subtype: "csv",
        entity_type: String(tags.entity_type || "GraphQL"),
        filters: tags,
        selected_columns: keys,
        schedule: {
          cadence: timeFrame,
          recipient_count: subscribers.length,
        },
        source: "csv_report_scheduler",
      });
      UseToast().show({
        type: ToastType.SUCCESS,
        msg: "Your report is now scheduled",
        duration: 10_000,
        action: {
          name: "View Scheduled Reports",
          onClick: () => {
            if (!SharedState.restrictions.is_restricted_app("reports")) {
              router().push({
                name: "Reports",
                query: { tab: "scheduled", subtab: "csv" },
              });
            }
          },
        },
      });
      const res = await api.get_csv_report_schedule_items();
      if (res.success) {
        scheduled_reports.value = res.value;
      }
    } else {
      const msg =
        create_csv_res.code == 409
          ? "a report with this configuration already exists"
          : "Something went wrong";

      UseToast().show({
        type: ToastType.ALERT,
        msg,
        duration: 5000,
      });
    }
    console.log("create_csv_res", create_csv_res);
  }
};

const create_gql_report = async (payload: {
  possible_call_tags: GqlCallsKey[];
  title: string;
  table_config: ITableConfig;
  tags: Record<string, string | number>;
}) => {
  init();
  const { possible_call_tags, table_config, tags, title } = payload;
  const mappings = table_config.columns.reduce((acc, col) => {
    if (col.jsPath) {
      acc.push({ [col.jsPath]: col.name });
    }
    return acc;
  }, [] as Array<Record<string, string>>);
  console.log("mappings", mappings);

  const query = GqlCache.get_query_from_last_tag_out_of_many(
    possible_call_tags
  );

  console.log(mappings, strip_off_cursor(query));
  const keys = mappings.map((item) => Object.values(item)[0]);
  const res = await UseModal().show(GenerateCsvReportModal, { keys, title });
  if (res.type == "success") {
    // @ts-ignore
    const { keys, name } = res.msg! as { keys: string[]; name: string };
    const create_csv_res = await api.create_csv_report({
      type: "GraphQL",
      graphql: {
        map: mappings.filter((item) => keys.includes(Object.values(item)[0])),
        query,
      },
      name: name,
      // get the tags from the filters
      tags: Object.entries(tags)
        // remove empty values
        .filter(([, v]) => v)
        .map(([key, value]) => `${key}:${value}`),
    });
    if (create_csv_res.success) {
      const created_report = create_csv_res.value as unknown as { id?: string };
      trackReportEvent("generated", {
        report_id: created_report.id,
        report_name: name,
        report_type: "csv",
        report_subtype: "GraphQL",
        entity_type: String(tags.entity_type || "GraphQL"),
        filters: tags,
        selected_columns: keys,
        source: "csv_report_generator",
      });
      UseToast().show({
        type: ToastType.SUCCESS,
        msg: "Your report is in the making",
        duration: 10_000,
        action: {
          name: "View All Reports",
          onClick: () => {
            if (!SharedState.restrictions.is_restricted_app("reports")) {
              router().push({ name: "Reports", query: { tab: "csv" } });
            }
          },
        },
      });
      csv_reports.mutate((list) => {
        return [
          CognniCsvReportHelper.from_api_item(create_csv_res.value),
          ...list,
        ];
      });
    } else {
      UseToast().show({
        type: ToastType.ALERT,
        msg: "Something went wrong",
        duration: 3000,
      });
    }
    console.log("create_csv_res", create_csv_res);
  }
};

const delete_report = async (report_id: string) => {
  const report = csv_reports.items.value.find((item) => item.id === report_id);
  const should_delete_res = await UseModal().showSimple({
    title: "Delete report",
    desc: `Are you sure you want to delete <br/> ${report?.name}?`,
    submitBtnText: "Delete",
    cancelBtnText: "Cancel",
  });
  if (should_delete_res.type == "success") {
    const res = await api.delete_report(report_id);
    if (res.success) {
      csv_reports.mutate((list) => {
        return list.filter((item) => item.id !== report_id);
      });
      if (report) {
        trackReportEvent("deleted", {
          report_id: report.id,
          report_name: report.name,
          report_type: "csv",
          report_subtype: report.type,
          entity_type: report.entity_type,
          status: report.status,
          parameters: report.params,
          source: "csv_reports_table",
        });
      }
    } else {
      UseToast().show({
        type: ToastType.ALERT,
        msg: "Something went wrong",
        duration: 3000,
      });
    }
  }
};

const start_checking_reports_status = () => {
  setInterval(async () => {
    const items_which_are_not_finishes = csv_reports.items.value
      .filter((item) => item.status !== "Ready" && item.status !== "Failed")
      .map((d) => d.id);

    if (items_which_are_not_finishes.length > 0) {
      const res = await api.load_some_csv_reports(items_which_are_not_finishes);
      if (res.success) {
        const ready_items = res.value.objects.filter(
          (d) =>
            d.status == "Ready" &&
            csv_reports.items.value.find((a) => a.id === d.id)!.status !==
              "Ready"
        );
        csv_reports.update_items_by("id", res.value.objects);
        if (ready_items.length > 0) {
          ready_items.forEach((report) => {
            NotificationsManager.addSimpleAction({
              name: ".csv report is ready",
              icon: "CSV",
              id: ready_items[0].id,
              type: "simple-action",
              group: "csv-reports", //  "in-progress" | "done" | "failed"
              status:
                report.status == "Ready"
                  ? "done"
                  : report.status == "Failed"
                  ? "failed"
                  : "in-progress",
              actions:
                report.status == "Ready"
                  ? [
                      {
                        icon: "Download",
                        name: "Download",
                        action: async (notification) => {
                          const res = await api.download_csv_report({
                            report_name: report.name,
                            report_id: report.id,
                          });
                          if (res.success) {
                            trackReportEvent("downloaded", {
                              report_id: report.id,
                              report_name: report.name,
                              report_type: "csv",
                              report_subtype: report.type,
                              entity_type: report.entity_type,
                              status: report.status,
                              parameters: report.params,
                              source: "csv_report_ready_notification",
                            });
                          }
                          NotificationsManager.remove(notification.id);
                        },
                      },
                    ]
                  : [],
            });
          });
        }
      }
    }
  }, 10_000);
};

const init = async () => {
  await data_sources_state.init();
  if (SharedState.FeatureHelper.is_enabled("csv_report_scheduler")) {
    const res = await api.get_csv_report_schedule_items();
    if (res.success) {
      scheduled_reports.value = res.value;
    }
  }
  await csv_reports.load_items({ cursor: "" });
  start_checking_reports_status();
  console.log("csv_reports.items", csv_reports.items);
};

export const state = {
  init,
  delete_report,
  create_gql_report,
  scheduled_reports,
  update_scheduled_report,
  remove_scheduled_report,
  schedule_gql_csv_report,
  ...csv_reports,
};
